import { TGDExerciseType, TGDRule, TGDRuleSpec, TGDRuleType } from
  '../../../models/courses/graphicDesign';

/** Finds a rule by type while preserving its typed parameters. */
export const findRule = <K extends TGDRuleType>(rules: TGDRule[], type: K) =>
  rules.find((rule): rule is Extract<TGDRule, { type: K }> => rule.type === type);

const designTypes: TGDRuleType[] = [
  'requiredElement', 'elementCountMax', 'maxColors', 'minColors', 'contrastMin',
  'usesPalette', 'sizeRatioMin', 'hierarchyOrder', 'alignedOn', 'timeMax',
];
export const DESIGN_RULE_TYPES = designTypes.filter(type => type !== 'timeMax');
export const RULE_TYPES_BY_EXERCISE: Record<TGDExerciseType, TGDRuleType[]> = {
  CREATE: designTypes,
  TRANSFORM: [...designTypes, 'changed', 'preserved'],
  INTENT: [...designTypes, 'choiceEquals', 'reasonEquals'],
  CRITIQUE: ['zoneFound', 'timeMax'],
  CHOOSE: ['choiceEquals', 'reasonEquals', 'timeMax'],
};
export const RULE_TYPE_LABELS_FR: Record<TGDRuleType, string> = {
  requiredElement: 'Élément obligatoire', elementCountMax: 'Nombre maximal d’éléments',
  maxColors: 'Nombre maximal de couleurs', minColors: 'Nombre minimal de couleurs',
  contrastMin: 'Contraste minimal', usesPalette: 'Harmonie de palette',
  sizeRatioMin: 'Rapport de taille minimal', hierarchyOrder: 'Ordre de lecture',
  alignedOn: 'Alignement', timeMax: 'Temps maximal', zoneFound: 'Zone trouvée',
  choiceEquals: 'Choix attendu', reasonEquals: 'Raison attendue',
  changed: 'Propriété modifiée', preserved: 'Propriété conservée',
};
export const RULE_TYPE_HELP_FR: Record<TGDRuleType, string> = {
  requiredElement: 'Un texte ou un élément doit être présent sur l’affiche.',
  elementCountMax: 'L’affiche ne doit pas contenir trop d’éléments.',
  maxColors: 'L’affiche ne doit pas utiliser trop de couleurs.',
  minColors: 'L’affiche doit utiliser assez de couleurs.',
  contrastMin: 'Un élément doit bien se détacher de ce qui est derrière.',
  usesPalette: 'Les couleurs doivent bien fonctionner ensemble.',
  sizeRatioMin: 'Un élément doit être plus grand qu’un autre.',
  hierarchyOrder: 'L’œil doit lire les éléments dans cet ordre.',
  alignedOn: 'Les éléments doivent être alignés.',
  timeMax: 'L’affiche doit être livrée avant la limite de temps.',
  zoneFound: 'L’élève doit trouver une erreur sur le visuel.',
  choiceEquals: 'L’élève doit choisir la bonne version ou le bon mot.',
  reasonEquals: 'L’élève doit donner la bonne raison.',
  changed: 'Un élément doit avoir été modifié.',
  preserved: 'Un élément doit rester identique.',
};
const text = () => ({ by: 'text' as const, value: '' });
const specs: Record<TGDRuleType, TGDRuleSpec> = {
  requiredElement: { type: 'requiredElement', params: { select: text(), minCount: 1 } },
  elementCountMax: { type: 'elementCountMax', params: { value: 8 } },
  maxColors: { type: 'maxColors', params: { value: 3, includeBackground: true } },
  minColors: { type: 'minColors', params: { value: 2, includeBackground: true } },
  contrastMin: { type: 'contrastMin', params: { a: text(),
    b: { by: 'background' }, value: 4.5 } },
  usesPalette: { type: 'usesPalette', params: { harmony: 'complementary', toleranceDeg: 20 } },
  sizeRatioMin: { type: 'sizeRatioMin', params: { a: text(), b: text(), value: 2 } },
  hierarchyOrder: { type: 'hierarchyOrder', params: { order: [text(), text()] } },
  alignedOn: { type: 'alignedOn', params: { select: [text(), text()],
    axis: 'left', tolerance: 4 } },
  timeMax: { type: 'timeMax', params: { seconds: 480 } },
  zoneFound: { type: 'zoneFound', params: { zoneId: '' } },
  choiceEquals: { type: 'choiceEquals', params: { choiceId: '' } },
  reasonEquals: { type: 'reasonEquals', params: { reasonId: '' } },
  changed: { type: 'changed', params: { select: text(), prop: 'fill', minDelta: 10 } },
  preserved: { type: 'preserved', params: { select: text(), prop: 'fill', tolerance: 3 } },
};
export const setTimeLimit = (rules: TGDRule[], seconds: number | undefined): TGDRule[] => {
  if (seconds === undefined) return rules.filter(rule => rule.type !== 'timeMax');
  const first = rules.findIndex(rule => rule.type === 'timeMax');
  if (first >= 0) return rules.map((rule, index) => index === first && rule.type === 'timeMax'
    ? { ...rule, params: { ...rule.params, seconds } } : rule);
  const next = createDefaultRule('timeMax', rules.map(rule => rule.id)) as
    Extract<TGDRule, { type: 'timeMax' }>;
  return [...rules, { ...next, params: { seconds }, tier: 'bonus', label: {
    fr: `Livré en moins de ${Math.round(seconds / 60)} min`,
  } }];
};
export const createDefaultRule = (type: TGDRuleType, existingIds: string[]): TGDRule => {
  let number = 1;
  while (existingIds.includes(`r${number}`)) number += 1;
  return { ...specs[type], id: `r${number}`, tier: 'required',
    label: { fr: RULE_TYPE_LABELS_FR[type] } };
};
